import type { InfoClient } from "@nktkas/hyperliquid";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { queryKeys } from "#/hooks/query-keys";
import { getInfoClient, getSubscriptionClient } from "../lib/hyperliquid";

type OrderBookSnapshot = NonNullable<Awaited<ReturnType<InfoClient["l2Book"]>>>;

export function useBestBidAsk(apiCoin: string | null): {
	bestBid: string | undefined;
	bestAsk: string | undefined;
} {
	const infoClient = getInfoClient();
	const [snapshot, setSnapshot] = useState<OrderBookSnapshot | null>(null);

	useEffect(() => {
		setSnapshot(null);
	}, [apiCoin]);

	useQuery({
		queryKey: queryKeys.trade.bestBidAsk(apiCoin ?? ""),
		queryFn: async () => {
			const result = (await infoClient.l2Book({
				coin: apiCoin as string,
			})) as OrderBookSnapshot | null;
			if (result) setSnapshot(result);
			return result;
		},
		enabled: apiCoin !== null,
		staleTime: 300_000,
		refetchInterval: false,
	});

	useEffect(() => {
		if (apiCoin === null) return;
		const client = getSubscriptionClient();
		if (!client) return;

		let cancelled = false;
		let unsubscribe: (() => Promise<void>) | null = null;

		client
			.l2Book({ coin: apiCoin }, (payload) => {
				if (cancelled) return;
				setSnapshot(payload as OrderBookSnapshot);
			})
			.then((subscription) => {
				if (cancelled) {
					void subscription.unsubscribe();
					return;
				}
				unsubscribe = () => subscription.unsubscribe();
			})
			.catch(() => {});

		return () => {
			cancelled = true;
			if (unsubscribe) void unsubscribe();
		};
	}, [apiCoin]);

	return {
		bestBid: snapshot?.levels[0]?.[0]?.px,
		bestAsk: snapshot?.levels[1]?.[0]?.px,
	};
}
